import React, { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { usePendientes } from "../hooks/usePendientes";
import { useClientes } from "../hooks/useClientes";
import { formatAmount } from "../config/currencies";
import {
  X, CircleCheck, TrendingDown, ArrowUpDown, Contact, Plus,
  Paperclip, Hourglass, Save, Smartphone,
} from "lucide-react";
import { uploadAttachment, deleteAttachment, normalizeReceiptImage, getAttachmentUrl } from "./receiptOcr";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// Convierte el "datetime" guardado ("DD/MM/AAAA HH:MM") al formato que
// espera <input type="datetime-local"> ("YYYY-MM-DDTHH:mm"). Si la entrada
// es antigua y no tiene ese campo, se reconstruye algo razonable a partir
// de mes/año (día 1, medianoche) para no dejar el selector vacío.
function toDtLocal(datetime, month, year) {
  if (datetime) {
    const m = datetime.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
    if (m) {
      const [, d, mo, y, hh, mi] = m;
      return `${y}-${mo}-${d}T${hh}:${mi}`;
    }
  }
  const idx = MONTHS.indexOf(month);
  const mo = idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
  return `${year || new Date().getFullYear()}-${mo}-01T00:00`;
}

// Formulario de edición — MISMA estructura que "Añadir entrada" (Simple
// siempre visible + "Añadir detalles" plegable, con la Operación combinada
// en una posición fija que sustituye los campos de su sitio sin mover nada
// más), pero en una ventana superpuesta ya rellena con los datos de la
// entrada. Al abrirse/cerrarse no toca la lista de debajo (Historial o
// Pendientes), que se queda exactamente donde estaba — es justo lo que
// antes hacía "saltar" la lista al editar en línea dentro de la fila.
export default function EditEntryModal({ entry, onClose }) {
  const { profile, lang, currency, user } = useAuth();
  const { updateEntry, deleteEntry, isPro } = useEntries();
  const { addPendiente } = usePendientes();
  const { clientes, addCliente } = useClientes();
  const c = profile?.config || {};
  const fmtAmt = n => formatAmount(n, currency);
  const isNegocio = profile?.perfil === "negocio";

  const [isCombined, setIsCombined] = useState(!!entry.isCombined);
  const [type, setType] = useState(entry.type || "expense");
  const [cat, setCat] = useState(entry.cat || "");
  const [desc, setDesc] = useState(entry.desc || "");
  const [payment, setPayment] = useState(entry.payment || "");
  const [amount, setAmount] = useState(entry.amount != null ? String(entry.amount) : "");
  const [incomeAmount, setIncomeAmount] = useState(
    entry.incomeAmount != null ? String(entry.incomeAmount) : (entry.type === "income" ? String(entry.amount || "") : "")
  );
  const [expenseAmount, setExpenseAmount] = useState(
    entry.expenseAmount != null ? String(entry.expenseAmount) : (entry.type === "expense" ? String(entry.amount || "") : "")
  );
  const [expenseDesc, setExpenseDesc] = useState(entry.expenseDesc || "");
  const [dtLocal, setDtLocal] = useState(toDtLocal(entry.datetime, entry.month, entry.year));

  const [clienteId, setClienteId] = useState(entry.clienteId || "");
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const clientesFiltrados = clienteSearch.trim()
    ? clientes.filter(cl => {
        const q = clienteSearch.trim().toLowerCase();
        return (cl.nombre||"").toLowerCase().includes(q)
            || (cl.telefono||"").toLowerCase().includes(q)
            || (cl.dni||"").toLowerCase().includes(q)
            || (cl.email||"").toLowerCase().includes(q);
      })
    : clientes;
  const [creatingCliente, setCreatingCliente] = useState(false);
  const [nuevoClienteNombre, setNuevoClienteNombre] = useState("");
  const [nuevoClienteTelefono, setNuevoClienteTelefono] = useState("");
  const [savingCliente, setSavingCliente] = useState(false);
  const contactPickerSupported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
  const soloDigitos = s => (s || "").replace(/\D/g, "");

  const activeCustomFields = isNegocio
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];
  const [customValues, setCustomValues] = useState(() => {
    const v = {};
    (c.custom_fields || []).forEach((f, i) => { v[`custom${i + 1}`] = entry[`custom${i + 1}`] || ""; });
    return v;
  });

  const [toPendiente, setToPendiente] = useState(false);
  const [pendienteRealizado, setPendienteRealizado] = useState("");
  const [pendienteDue, setPendienteDue] = useState("");

  // Adjunto — a diferencia de Añadir (donde siempre es un archivo nuevo),
  // aquí puede que ya hubiera uno guardado: se puede ver, reemplazar por
  // otro, o quitar. Nada se borra de Storage hasta que se guarde de verdad.
  const [existingReceiptPath, setExistingReceiptPath] = useState(entry.receiptPath || null);
  const [removeExistingReceipt, setRemoveExistingReceipt] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptFileName, setReceiptFileName] = useState("");
  const [attachProcessing, setAttachProcessing] = useState(false);
  const attachInputRef = useRef(null);

  const [detailMode, setDetailMode] = useState(false);
  const showDetails = detailMode;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cats = type === "income" ? (c.income_cats || []) : (c.expense_cats || []);
  const combinedCats = [...new Set([...(c.income_cats || []), ...(c.expense_cats || [])])];
  const accounts = (c.accounts || []).map(a => a.name);
  const combinedTotal = (parseFloat((incomeAmount||"0").replace(",",".")) || 0)
                       - (parseFloat((expenseAmount||"0").replace(",",".")) || 0);

  async function handleAttachFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    setAttachProcessing(true);
    try {
      const finalFile = isPdf ? file : await normalizeReceiptImage(file);
      setReceiptFile(finalFile);
      setReceiptFileName(file.name || (lang==="en" ? "Attachment" : "Adjunto"));
      setRemoveExistingReceipt(false);
    } finally {
      setAttachProcessing(false);
    }
  }

  async function handleVerAdjuntoActual() {
    try {
      const url = await getAttachmentUrl(existingReceiptPath);
      window.open(url, "_blank");
    } catch (err) {
      console.error("No se pudo abrir el adjunto:", err);
      alert(lang==="en" ? "Couldn't open the file" : "No se pudo abrir el archivo");
    }
  }

  async function handleImportarContactoRapido() {
    if (!contactPickerSupported) return;
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (!picked || picked.length === 0) return;
      const p = picked[0];
      const nombre = (p.name && p.name[0]) || "";
      const telefono = (p.tel && p.tel[0]) || "";
      const normTel = soloDigitos(telefono);
      const existente = normTel ? clientes.find(cl => soloDigitos(cl.telefono) === normTel) : null;
      if (existente) {
        const usarExistente = window.confirm(
          lang==="en"
            ? `You already have a contact with this phone: "${existente.nombre}". Use that one instead of creating a new contact?`
            : `Ya tienes un contacto guardado con este teléfono: "${existente.nombre}". ¿Quieres usar ese contacto en vez de crear uno nuevo?`
        );
        if (usarExistente) {
          setClienteId(existente.id);
          setCreatingCliente(false);
          setNuevoClienteNombre(""); setNuevoClienteTelefono("");
          return;
        }
      }
      if (!nombre) {
        setNuevoClienteNombre(nombre);
        setNuevoClienteTelefono(telefono);
        return;
      }
      await handleCrearClienteRapido(nombre, telefono);
    } catch (err) {
      console.warn("Selector de contactos cancelado:", err);
    }
  }

  async function handleCrearClienteRapido(nombreArg, telefonoArg) {
    // Si esta función se usa directamente como onClick de un botón, React
    // entrega el propio evento del click como primer argumento — no un
    // texto — y "eventoDeClick.trim()" rompía la función antes de llegar
    // al try/catch: no se guardaba nada, no salía ningún error, y el
    // botón parecía no hacer nada. Por eso solo se usan estos argumentos
    // si de verdad son texto (vienen de importar un contacto); si no, se
    // usan los campos del formulario, que es lo correcto al pulsar Guardar.
    const nombreFinal = (typeof nombreArg === "string" ? nombreArg : nuevoClienteNombre).trim();
    const telefonoFinal = (typeof telefonoArg === "string" ? telefonoArg : nuevoClienteTelefono).trim();
    if (!nombreFinal) return;
    setError("");
    setSavingCliente(true);
    try {
      const newId = await addCliente({
        nombre: nombreFinal,
        ...(telefonoFinal ? { telefono: telefonoFinal } : {}),
      });
      setClienteId(newId);
      setCreatingCliente(false);
      setNuevoClienteNombre("");
      setNuevoClienteTelefono("");
    } catch (err) {
      setError(lang==="en" ? `Couldn't create contact: ${err.message||err}` : `No se pudo crear el contacto: ${err.message||err}`);
    } finally {
      setSavingCliente(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (!dtLocal) {
      setError(lang==="en" ? "Enter a valid date" : "Introduce una fecha válida");
      return;
    }
    const [datePart, timePart] = dtLocal.split("T");
    const [y, mo, d] = datePart.split("-");
    const datetimeToSave = `${d}/${mo}/${y} ${timePart || "00:00"}`;
    const parsedMonth = MONTHS[parseInt(mo) - 1];
    const parsedYear = parseInt(y);

    setLoading(true);
    try {
      // Adjunto: se sube el nuevo (si lo hay, reemplazando al anterior en
      // Storage) o se borra el actual si se pidió quitarlo sin sustituto.
      let finalReceiptPath = existingReceiptPath;
      if (isPro && receiptFile) {
        if (existingReceiptPath) await deleteAttachment(existingReceiptPath);
        finalReceiptPath = await uploadAttachment(receiptFile, user.uid);
      } else if (removeExistingReceipt && existingReceiptPath) {
        await deleteAttachment(existingReceiptPath);
        finalReceiptPath = null;
      }

      const customVals = {};
      activeCustomFields.forEach(f => { customVals[f.key] = customValues[f.key] || ""; });

      // Destinar a pendiente: convierte esta entrada (entera o en parte) en
      // un pendiente — misma lógica que ya había, solo reordenada aquí.
      if (toPendiente) {
        const totalAmt = parseFloat((amount||"0").replace(",",".")) || 0;
        const realizadoAmt = Math.max(0, parseFloat((pendienteRealizado||"0").replace(",",".")) || 0);
        if (realizadoAmt > totalAmt) {
          setError(lang==="en" ? "The amount already received/paid can't be higher than the total" : "Lo ya cobrado/pagado no puede ser mayor que el importe total");
          setLoading(false);
          return;
        }
        const restanteAmt = Math.max(0, totalAmt - realizadoAmt);

        if (realizadoAmt > 0) {
          await updateEntry(entry.id, {
            type, cat: cat || "General", desc, payment, amount: realizadoAmt,
            month: parsedMonth, year: parsedYear, datetime: datetimeToSave,
            clienteId: clienteId || null,
            ...(finalReceiptPath ? { receiptPath: finalReceiptPath } : { receiptPath: null }),
            ...customVals,
          });
        } else {
          await deleteEntry(entry.id);
        }
        if (restanteAmt > 0) {
          await addPendiente({
            type, desc, cat: cat || "General", payment, amount: restanteAmt,
            due: pendienteDue || null,
            clienteId: clienteId || null,
            ...(finalReceiptPath ? { receiptPath: finalReceiptPath } : {}),
            ...customVals,
          });
        }
        setLoading(false);
        onClose();
        return;
      }

      let payload;
      if (isCombined) {
        const incAmt = parseFloat((incomeAmount||"0").replace(",",".")) || 0;
        const expAmt = parseFloat((expenseAmount||"0").replace(",",".")) || 0;
        if (incAmt <= 0 && expAmt <= 0) {
          setError(lang==="en" ? "Enter an income and/or an expense amount" : "Introduce un importe de ingreso y/o de gasto");
          setLoading(false);
          return;
        }
        const net = incAmt - expAmt;
        payload = {
          type: net >= 0 ? "income" : "expense",
          cat: cat || "General", desc, expenseDesc: expenseDesc || "", payment,
          amount: Math.abs(net),
          isCombined: true, incomeAmount: incAmt, expenseAmount: expAmt,
          month: parsedMonth, year: parsedYear, datetime: datetimeToSave,
          clienteId: clienteId || null,
          ...(finalReceiptPath ? { receiptPath: finalReceiptPath } : { receiptPath: null }),
          ...customVals,
        };
      } else {
        const amt = parseFloat((amount||"0").replace(",","."));
        if (!amt || amt <= 0) {
          setError(lang==="en" ? "Enter a valid amount" : "Introduce un importe válido");
          setLoading(false);
          return;
        }
        payload = {
          type, cat: cat || "General", desc, payment, amount: amt,
          month: parsedMonth, year: parsedYear, datetime: datetimeToSave,
          clienteId: clienteId || null,
          // Se limpian explícitamente por si esta entrada venía de ser
          // combinada — si no, esos campos se quedan "pegados" con sus
          // valores antiguos aunque ya no correspondan.
          isCombined: false, incomeAmount: null, expenseAmount: null, expenseDesc: null,
          ...(finalReceiptPath ? { receiptPath: finalReceiptPath } : { receiptPath: null }),
          ...customVals,
        };
      }
      await updateEntry(entry.id, payload);
      setLoading(false);
      onClose();
    } catch (err) {
      console.error("Error al guardar la edición:", err);
      setLoading(false);
      setError(lang==="en" ? `Error: ${err.message||err}` : `Error: ${err.message||err}`);
    }
  }

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.5)", zIndex:100,
                 display:"flex", alignItems:"flex-start", justifyContent:"center",
                 padding:"5vh 16px", overflowY:"auto"}}
         onClick={onClose}>
      <div className="card" style={{maxWidth:520, width:"100%", margin:0}} onClick={ev=>ev.stopPropagation()}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
          <h3 style={{fontSize:16,fontWeight:700,display:"flex",alignItems:"center",gap:6,margin:0}}>
            {lang==="en" ? "Edit entry" : "Editar entrada"}
          </h3>
          <button type="button" onClick={onClose} aria-label={lang==="en"?"Close":"Cerrar"}
                  style={{background:"none",border:"none",cursor:"pointer",color:"var(--gray-400)",display:"flex"}}>
            <X size={20}/>
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSave}>
          {/* Adjuntar foto o documento — función Pro. Si ya había uno
              guardado, se puede ver, reemplazar o quitar. Va siempre arriba del todo. */}
          <div className="form-group">
            <label className="form-label">
              <Paperclip size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:4}}/> {lang==="en" ? "Attach photo or file (optional)" : "Adjuntar foto o documento (opcional)"}
            </label>
            {!isPro ? (
              <p className="form-hint">
                {lang==="en" ? "Attaching files to an entry is a Pro feature — " : "Adjuntar archivos a una entrada es una función Pro — "}
              </p>
            ) : receiptFile ? (
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
                           padding:"10px 12px", background:"var(--gray-50)", borderRadius:8}}>
                <span style={{fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {receiptFileName}
                </span>
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>{ setReceiptFile(null); setReceiptFileName(""); }}>
                  {lang==="en" ? "Remove" : "Quitar"}
                </button>
              </div>
            ) : existingReceiptPath && !removeExistingReceipt ? (
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
                           padding:"10px 12px", background:"var(--gray-50)", borderRadius:8}}>
                <span style={{fontSize:13, color:"var(--blue)", cursor:"pointer", fontWeight:600}} onClick={handleVerAdjuntoActual}>
                  {lang==="en" ? "View current file" : "Ver archivo actual"}
                </span>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={()=>attachInputRef.current?.click()}>
                    {lang==="en" ? "Replace" : "Reemplazar"}
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={()=>setRemoveExistingReceipt(true)}>
                    {lang==="en" ? "Remove" : "Quitar"}
                  </button>
                </div>
              </div>
            ) : existingReceiptPath && removeExistingReceipt ? (
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
                           padding:"10px 12px", background:"var(--red-l)", borderRadius:8}}>
                <span style={{fontSize:13, color:"var(--red)"}}>
                  {lang==="en" ? "Will be removed when you save" : "Se quitará al guardar"}
                </span>
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>setRemoveExistingReceipt(false)}>
                  {lang==="en" ? "Undo" : "Deshacer"}
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-outline" disabled={attachProcessing}
                      style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6}}
                      onClick={()=>attachInputRef.current?.click()}>
                <Paperclip size={16}/> {attachProcessing
                  ? (lang==="en" ? "Processing..." : "Procesando...")
                  : (lang==="en" ? "Attach" : "Adjuntar")}
              </button>
            )}
            <input ref={attachInputRef} type="file" accept="image/*,application/pdf"
                   style={{display:"none"}} onChange={handleAttachFile} />
          </div>

          {/* Operación combinada — posición fija, igual que en Añadir Entrada. */}
          {isNegocio && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={isCombined}
                       onChange={e=>{
                         const checked = e.target.checked;
                         if (checked) {
                           setIncomeAmount(type==="income" ? amount : "0");
                           setExpenseAmount(type==="expense" ? amount : "0");
                         } else {
                           const inc = parseFloat((incomeAmount||"0").replace(",",".")) || 0;
                           const exp = parseFloat((expenseAmount||"0").replace(",",".")) || 0;
                           const net = inc - exp;
                           setType(net >= 0 ? "income" : "expense");
                           setAmount(String(Math.abs(net)));
                         }
                         setIsCombined(checked);
                       }}
                       style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)"}}>
                  <ArrowUpDown size={14} style={{marginRight:2,display:"inline",verticalAlign:"-2px"}}/> {lang==="en" ? "Combined operation (income + expense together)" : "Operación combinada (ingreso + gasto juntos)"}
                </span>
              </label>
            </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{lang==="en"?"Type":"Tipo"}</label>
            <div style={{display:"flex",gap:10}}>
              {[["income",(c.income_label||(lang==="en"?"Income":"Ingresos")),CircleCheck],["expense",(c.expense_label||(lang==="en"?"Expenses":"Gastos")),TrendingDown]].map(([v,l,Icon])=>(
                <label key={v} style={{flex:1,cursor:"pointer"}}>
                  <input type="radio" value={v} checked={type===v} onChange={()=>{setType(v);setCat("");}} style={{display:"none"}}/>
                  <div style={{padding:"10px 14px",borderRadius:"var(--radius-sm)",textAlign:"center",
                               border:`2px solid ${type===v?"var(--blue)":"var(--border)"}`,
                               background:type===v?"var(--blue-l)":"white",fontSize:14,fontWeight:500,
                               color:type===v?"var(--blue)":"var(--gray-700)",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                    <Icon size={15}/> {l}
                  </div>
                </label>
              ))}
            </div>
          </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{lang==="en"?"Category":"Categoría"}</label>
            <select className="form-select" value={cat} onChange={e=>setCat(e.target.value)}>
              <option value="">{lang==="en"?"Select a category":"Selecciona una categoría"}</option>
              {cats.map(cc=><option key={cc}>{cc}</option>)}
              <option value="Otro">{lang==="en"?"Other":"Otro"}</option>
            </select>
          </div>
          )}

          {isCombined && (
            <div className="form-group">
              <label className="form-label">{lang==="en"?"Category":"Categoría"}</label>
              <select className="form-select" value={cat} onChange={e=>setCat(e.target.value)}>
                <option value="">{lang==="en"?"Select a category":"Selecciona una categoría"}</option>
                {combinedCats.map(cc=><option key={cc}>{cc}</option>)}
                <option value="Otro">{lang==="en"?"Other":"Otro"}</option>
              </select>
            </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{lang==="en"?"Description":"Descripción"} <span style={{color:"var(--gray-400)",fontWeight:400}}>{lang==="en"?"(optional)":"(opcional)"}</span></label>
            <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)}
                   placeholder={lang==="en"?"E.g. supermarket, salary...":"Ej: supermercado, nómina..."} />
          </div>
          )}

          {isCombined && (
            <div className="form-group">
              <div className="form-row" style={{display:"flex",gap:10}}>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><CircleCheck size={13}/> {lang==="en" ? "Income amount" : "Importe Ingreso"}</label>
                  <input className="form-input" type="number" step="0.01" min="0"
                         value={incomeAmount} onChange={e=>setIncomeAmount(e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><TrendingDown size={13}/> {lang==="en" ? "Expense amount" : "Importe Gasto"}</label>
                  <input className="form-input" type="number" step="0.01" min="0"
                         value={expenseAmount} onChange={e=>setExpenseAmount(e.target.value)} placeholder="0.00" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><CircleCheck size={13}/> {lang==="en" ? "Income description" : "Descripción del ingreso"} <span style={{color:"var(--gray-400)",fontWeight:400}}>{lang==="en"?"(optional)":"(opcional)"}</span></label>
                <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)}
                       placeholder={lang==="en" ? "E.g. sale to customer..." : "Ej: venta a cliente..."} />
              </div>

              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><TrendingDown size={13}/> {lang==="en" ? "Expense description" : "Descripción del gasto"} <span style={{color:"var(--gray-400)",fontWeight:400}}>{lang==="en"?"(optional)":"(opcional)"}</span></label>
                <input className="form-input" value={expenseDesc} onChange={e=>setExpenseDesc(e.target.value)}
                       placeholder={lang==="en" ? "E.g. materials, fuel..." : "Ej: material, gasolina..."} />
              </div>

              <div style={{marginTop:8,padding:"10px 14px",borderRadius:"var(--radius-sm)",
                           background:combinedTotal>=0?"var(--green-l,#f0fdf4)":"var(--red-l)",
                           border:`1px solid ${combinedTotal>=0?"var(--green)":"var(--red)"}`,
                           fontSize:14,fontWeight:700,color:combinedTotal>=0?"var(--green)":"var(--red)"}}>
                {lang==="en" ? "Net total" : "Total neto"}: {combinedTotal>=0?"+":""}{fmtAmt(combinedTotal)}
              </div>
            </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{lang==="en"?"Amount":"Importe"}</label>
            <input className="form-input" type="number" step="0.01" min="0.01"
                   value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" required />
          </div>
          )}

          <div className="form-group" style={{marginTop:6, marginBottom:showDetails?18:6}}>
            <button type="button" onClick={()=>setDetailMode(v=>!v)}
                    className="btn btn-outline"
                    style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,padding:"10px 14px"}}>
              <Plus size={16} style={{transform:showDetails?"rotate(45deg)":"none",transition:"transform .15s"}}/>
              {showDetails
                ? (lang==="en" ? "Hide details" : "Ocultar detalles")
                : (lang==="en" ? "Add details (date, contact, extras...)" : "Añadir detalles (fecha, contacto, extras...)")}
            </button>
          </div>

          {showDetails && (
          <>
          {accounts.length > 0 && (
            <div className="form-group">
              <label className="form-label">{lang==="en"?"Account":"Cuenta"}</label>
              <select className="form-select" value={payment} onChange={e=>setPayment(e.target.value)}>
                <option value="">{lang==="en"?"No account":"Sin cuenta"}</option>
                {accounts.map(a=><option key={a}>{a}</option>)}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{lang==="en"?"Date and time":"Fecha y hora"}</label>
            <input className="form-input" type="datetime-local" value={dtLocal}
                   onChange={e=>setDtLocal(e.target.value)}
                   style={{
                     width:"100%", boxSizing:"border-box", display:"block",
                     WebkitAppearance:"none", appearance:"none",
                     minHeight:44, lineHeight:"normal",
                   }} />
          </div>

          {isNegocio && (
            <div className="form-group">
              <label className="form-label">
                <Contact size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:4}}/> {lang==="en" ? "Contact (optional)" : "Contacto (opcional)"}
              </label>
              {clienteId && !creatingCliente ? (
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
                             padding:"10px 12px", background:"var(--gray-50)", borderRadius:8}}>
                  <span style={{fontWeight:600, fontSize:14}}>
                    {clientes.find(cl => cl.id === clienteId)?.nombre || "…"}
                  </span>
                  <button type="button" className="btn btn-outline btn-sm" onClick={()=>{ setClienteId(""); setClienteSearch(""); }}>
                    {lang==="en" ? "Change" : "Cambiar"}
                  </button>
                </div>
              ) : !creatingCliente ? (
                <div style={{position:"relative"}}>
                  <div style={{display:"flex", gap:8}}>
                    <input className="form-input" style={{flex:1}} value={clienteSearch}
                           onChange={e=>{ setClienteSearch(e.target.value); setClienteSearchOpen(true); }}
                           onFocus={()=>setClienteSearchOpen(true)}
                           placeholder={lang==="en" ? "Search contact by name..." : "Buscar contacto por nombre..."}/>
                    <button type="button" className="btn btn-outline" title={lang==="en" ? "New contact" : "Nuevo contacto"}
                            aria-label={lang==="en" ? "New contact" : "Nuevo contacto"}
                            style={{width:40,height:40,padding:0,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}
                            onClick={()=>{ setCreatingCliente(true); setClienteSearchOpen(false); setClienteSearch(""); }}>
                      <Plus size={18}/>
                    </button>
                  </div>
                  {clienteSearchOpen && (
                    <>
                      <div style={{position:"fixed", inset:0, zIndex:119}} onClick={()=>setClienteSearchOpen(false)} />
                      <div style={{position:"absolute", top:"100%", left:0, right:0, marginTop:4, zIndex:120,
                                   background:"var(--card,#fff)", border:"1px solid var(--border)", borderRadius:8,
                                   maxHeight:200, overflowY:"auto", boxShadow:"var(--shadow-md,0 6px 20px rgba(0,0,0,.15))"}}>
                        {clientesFiltrados.length === 0 ? (
                          <div style={{padding:"10px 12px", fontSize:13, color:"var(--gray-400)"}}>
                            {clientes.length===0
                              ? (lang==="en" ? "No contacts yet" : "Todavía no tienes contactos")
                              : (lang==="en" ? "No matches" : "Sin resultados")}
                          </div>
                        ) : clientesFiltrados.slice(0, 8).map(cl => (
                          <div key={cl.id}
                               onClick={()=>{ setClienteId(cl.id); setClienteSearch(""); setClienteSearchOpen(false); }}
                               style={{padding:"9px 12px", cursor:"pointer", fontSize:14}}
                               onMouseEnter={e=>e.currentTarget.style.background="var(--gray-50)"}
                               onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            {cl.nombre}
                          </div>
                        ))}
                        <div onClick={()=>{ setCreatingCliente(true); setClienteSearchOpen(false); }}
                             style={{padding:"9px 12px", cursor:"pointer", fontSize:14, fontWeight:700,
                                     color:"var(--gold-dark,#9C7A16)",
                                     borderTop: clientesFiltrados.length>0 ? "1px solid var(--border)" : "none"}}>
                          + {lang==="en" ? "New contact..." : "Nuevo contacto..."}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{display:"flex", flexDirection:"column", gap:8}}>
                  {contactPickerSupported && (
                    <button type="button" className="btn btn-outline btn-sm" style={{justifyContent:"center"}}
                            onClick={handleImportarContactoRapido}>
                      <Smartphone size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Import from my contacts" : "Importar desde mis contactos"}
                    </button>
                  )}
                  <div style={{display:"flex", gap:8}}>
                    <input className="form-input" autoFocus value={nuevoClienteNombre}
                           onChange={e=>setNuevoClienteNombre(e.target.value)}
                           placeholder={lang==="en" ? "New contact's name" : "Nombre del contacto nuevo"}
                           onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); handleCrearClienteRapido(); } }}/>
                    <input className="form-input" type="tel" style={{maxWidth:130}} value={nuevoClienteTelefono}
                           onChange={e=>setNuevoClienteTelefono(e.target.value)}
                           placeholder={lang==="en" ? "Phone" : "Teléfono"}
                           onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); handleCrearClienteRapido(); } }}/>
                  </div>
                  <div style={{display:"flex", gap:8}}>
                    <button type="button" className="btn btn-primary btn-sm" style={{flex:1,justifyContent:"center"}}
                            disabled={savingCliente || !nuevoClienteNombre.trim()} onClick={()=>handleCrearClienteRapido()}>
                      {savingCliente ? "..." : (lang==="en" ? "Save" : "Guardar")}
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" style={{flex:1,justifyContent:"center"}}
                            onClick={()=>{ setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch(""); }}>
                      {lang==="en" ? "Cancel" : "Cancelar"}
                    </button>
                  </div>
                  {error && (
                    <p style={{fontSize:12,color:"var(--red)",margin:0}}>{error}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeCustomFields.length > 0 && (
            <div className="form-group">
              <label className="form-label">{lang==="en" ? "Extra details" : "Datos adicionales"}</label>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {activeCustomFields.map(f => (
                  <input key={f.key} className="form-input"
                         placeholder={f.name}
                         value={customValues[f.key] || ""}
                         onChange={e=>setCustomValues(v=>({...v,[f.key]:e.target.value}))}/>
                ))}
              </div>
            </div>
          )}

          {/* Destinar a pendiente: por si te equivocaste al registrar esto,
              o no te han pagado/no has pagado el total — no aplica si la
              operación es combinada. */}
          {!isCombined && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={toPendiente}
                       onChange={e=>{ setToPendiente(e.target.checked); if(!e.target.checked){ setPendienteRealizado(""); setPendienteDue(""); } }}
                       style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)",display:"inline-flex",alignItems:"center",gap:5}}>
                  <Hourglass size={15}/> {lang==="en" ? "Send to Pending instead?" : "¿Destinar a pendiente?"}
                </span>
              </label>
              {toPendiente && (
                <>
                  <p className="form-hint">
                    {lang==="en"
                      ? "E.g. this was a mistake, or you weren't paid/didn't pay the full amount."
                      : "Ej: te equivocaste al registrarlo, o no te han pagado/no has pagado el importe completo."}
                  </p>
                  <div style={{marginTop:10}}>
                    <label className="form-label">
                      {type==="income"
                        ? (lang==="en" ? "Already received (optional)" : "Ya cobrado (opcional)")
                        : (lang==="en" ? "Already paid (optional)" : "Ya pagado (opcional)")}
                    </label>
                    <input className="form-input" type="number" step="0.01" min="0"
                           value={pendienteRealizado} onChange={e=>setPendienteRealizado(e.target.value)}
                           placeholder="0.00"/>
                  </div>
                  <div style={{marginTop:10}}>
                    <label className="form-label">
                      {lang==="en" ? "Due date (optional)" : "Fecha de vencimiento (opcional)"}
                    </label>
                    <input className="form-input" type="date" value={pendienteDue}
                           onChange={e=>setPendienteDue(e.target.value)}/>
                  </div>
                </>
              )}
            </div>
          )}
          </>
          )}

          <div style={{display:"flex",gap:8,marginTop:6}}>
            <button className="btn btn-primary" style={{flex:1,justifyContent:"center",padding:"12px"}}
                    type="submit" disabled={loading}>
              {loading
                ? (lang==="en"?"Saving...":"Guardando...")
                : <><Save size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Save":"Guardar"}</>}
            </button>
            <button type="button" className="btn btn-outline" style={{padding:"12px 18px"}} onClick={onClose} disabled={loading}>
              {lang==="en"?"Cancel":"Cancelar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
